import { useState } from 'react';
import { styled, useMediaQuery } from '@mui/material';
import {
  IPreFilter,
  DeaNumFunction,
  Error,
  ErrorIcon,
  SearchResp,
  RespData,
  useUserContext,
  csosUpmApi,
  IPageInfo,
  ErrorCode,
  variables,
} from '../common';
import { CsaFilterStack, CsaFilterTable, CsaPreFilter } from '../csafilter';
import { pathOr } from 'rambda';
import { flow } from 'fp-ts/function';

const defaultFilter: IPreFilter = {
  chainCodeFlag: true,
  deaNumber: null,
  ssn: null,
  taxId: null,
  npi: null,
  city: null,
  state: null,
  postalCd: null,
};

export const CsaFilterWrapper = ({
  deaNums,
  save,
  tooltipValue,
  toggleTooltip,
  offTooltip,
}: {
  deaNums: string[];
  save: DeaNumFunction;
  tooltipValue: (tooltip: string) => boolean;
  toggleTooltip: (tooltip: string) => void;
  offTooltip: (tooltip: string) => void;
}) => {
  const [csaSrchResult, setCsaSrchResult] = useState<SearchResp>(
    {} as SearchResp
  );
  const [filterFields, setFilterFields] = useState<IPreFilter>(defaultFilter);
  const { user } = useUserContext();

  const fetchDeaExtractData = flow(
    (payload: IPreFilter, pageInfo: IPageInfo) => {
      const { pageNumber, pageSize } = pageInfo;
      csosUpmApi(user.icam!.headers)
        .post(
          `v1/api/upm/legacy/csa-search?page=${pageNumber}&size=${pageSize}`,
          payload
        )
        .then(
          flow(
            (result): SearchResp => ({
              data: result.data as RespData,
              status: result.status || 0,
              apiCallStatus: true,
            }),
            setCsaSrchResult
          )
        )
        .catch(e =>
          setCsaSrchResult(a => ({
            ...a,
            apiCallStatus: false,
            error: {
              code: ErrorCode.CsaSearchError,
              message: pathOr(
                'Failed to search DeaNum',
                'response.data.message'
              )(e),
            },
          }))
        );
    },

    () => setCsaSrchResult(a => ({ ...a, apiCallStatus: null }))
  );

  const searchByFilter = (filter: IPreFilter, pageInfo: IPageInfo) => {
    setFilterFields(filter);
    fetchDeaExtractData(filter, pageInfo);
  };

  const searchByPage = (pageInfo: IPageInfo) => {
    fetchDeaExtractData(filterFields, pageInfo);
  };

  const clear = () => setCsaSrchResult(a => ({ ...a, data: { content: [] } }));

  const matchesGridBreakpoint = useMediaQuery('(min-width:60em)', {
    noSsr: true,
  });

  return (
    <>
      <CsaPreFilter
        emptyCsaSrchResult={pathOr(0, 'data.totalElements')(csaSrchResult) <= 0}
        searchByFilter={searchByFilter}
        tooltipValue={tooltipValue}
        toggleTooltip={toggleTooltip}
        offTooltip={offTooltip}
        onClear={clear}
      />
      <Actions>
        {csaSrchResult.apiCallStatus === null ? (
          <h2 style={{ color: variables.brandInfo }}>
            <span>Loading DeaNum...</span>
          </h2>
        ) : csaSrchResult.apiCallStatus === false ? (
          <span>
            <ErrorIcon />
            <Error>
              {pathOr(
                'Failed to search DeaNum',
                'message'
              )(csaSrchResult.error)}
            </Error>
          </span>
        ) : matchesGridBreakpoint ? (
          <CsaFilterTable
            deaNums={deaNums}
            searchResult={csaSrchResult}
            save={save}
            searchByPage={searchByPage}
          />
        ) : (
          <CsaFilterStack
            as={pathOr([], 'data.content')(csaSrchResult)}
            totalPages={pathOr(0, 'data.totalPages')(csaSrchResult)}
            size={pathOr(0, 'data.size')(csaSrchResult)}
            searchByPage={searchByPage}
            selectedDeaNums={deaNums}
            save={save}
          />
        )}
      </Actions>
    </>
  );
};

const Actions = styled('article')(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',

  justifyContent: 'center',
  '@media (min-width: 40em)': {
    '&>*+*': {
      marginLeft: '2.5rem',
    },
  },
}));
